import { useState } from "react";
import * as Dropdown from "@radix-ui/react-dropdown-menu";
import { useNavigate } from "react-router-dom";
import {
  Check,
  ChevronDown,
  ChevronRight,
  LogOut,
  MessageSquare,
  Monitor,
  Moon,
  Palette,
  Receipt,
  RefreshCcw,
  ShieldCheck,
  Sun,
  User,
} from "lucide-react";
import { isRoyalTeamDepartment } from "@mtform/shared";
import { useAuth } from "@/auth/AuthContext";
import {
  SyncTriplinkMenuSection,
  useTriplinkSync,
} from "@/components/admin/SyncTriplinkControls";
import { FeedbackDialog } from "@/components/feedback/FeedbackDialog";
import {
  useTheme,
  type ResolvedThemePreference,
} from "@/theme/ThemeContext";
import { cn } from "@/lib/utils";

// =============================================================================
// AccountMenu — top-right avatar dropdown
//
// Shown only when the user is signed in (the layout doesn't render it
// otherwise). Pulls user data from AuthContext, lets the user open the
// admin panel (Admin / Super Admin only) or sign out.
// =============================================================================

export function AccountMenu() {
  const navigate = useNavigate();
  const { state, signOut } = useAuth();
  const { preference, setPreference } = useTheme();
  const [open, setOpen] = useState(false);
  // Lift the FeedbackDialog's open-state up here so the avatar menu
  // owns the lifecycle. Radix's `Dropdown.Item` auto-closes the menu
  // on select, which is what we want — feedback opens on top of the
  // (now-closed) dropdown rather than nested inside it.
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  // Royal-team membership is derived BEFORE the sync hook (and guarded for
  // the unauthenticated case) so it can gate the hook's status polling —
  // a non-Royal admin must never poll the Royal-gated status endpoint
  // (it'd 403 on every menu open). `state.user` is only present once
  // authenticated; we fall back to null so the hooks below stay
  // unconditional (rules of hooks).
  const authedUser = state.status === "authenticated" ? state.user : null;
  const isRoyalTeamMember =
    !!authedUser &&
    (authedUser.role === "admin" || authedUser.role === "super_admin") &&
    isRoyalTeamDepartment(authedUser.department);

  // The sync hook owns the mutations + a live status poll. It's cheap to
  // keep mounted unconditionally (rules of hooks) regardless of role or
  // dropdown state. It lives on the AccountMenu — not the dropdown — so a
  // Backfill confirm, which closes the menu by stealing focus, never
  // unmounts the in-flight mutation (or stops the poll mid-run). Polling
  // is enabled only while a Royal-team member's dropdown is open; it also
  // self-sustains through a local run regardless of menu state.
  const sync = useTriplinkSync({ pollStatus: open && isRoyalTeamMember });

  if (state.status !== "authenticated") return null;
  const { user } = state;

  const initials = `${user.firstName.charAt(0) ?? ""}${user.lastName.charAt(0) ?? ""}`
    .trim()
    .toUpperCase()
    .slice(0, 2) || user.email.charAt(0).toUpperCase();
  const fullName = `${user.firstName} ${user.lastName}`.trim() || user.email;
  const isAdmin = user.role === "admin" || user.role === "super_admin";
  const roleLabel = friendlyRole(user.role);

  async function handleSignOut() {
    await signOut();
    navigate("/login", { replace: true });
  }

  return (
    <Dropdown.Root open={open} onOpenChange={setOpen}>
      <Dropdown.Trigger asChild>
        <button
          type="button"
          aria-label="Account menu"
          className="inline-flex min-w-0 items-center gap-2 rounded-full border border-foreground/15 bg-foreground/[0.04] py-1.5 pl-1.5 pr-3 text-sm font-semibold text-foreground transition hover:border-foreground/25 hover:bg-foreground/[0.07] focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary/40 to-[hsl(var(--accent-2)/0.3)] text-xs font-bold text-foreground">
            {initials}
          </span>
          <span className="-mx-px hidden truncate px-px text-foreground sm:inline">{fullName}</span>
          <ChevronDown
            className={cn(
              "h-4 w-4 shrink-0 text-muted-foreground transition",
              open && "rotate-180",
            )}
          />
        </button>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content
          align="end"
          sideOffset={6}
          className="z-50 w-[min(20rem,calc(100vw-2rem))] overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-2xl"
        >
          <div className="flex flex-col gap-0.5 border-b border-border px-3 py-2.5">
            <span className="text-sm font-bold text-foreground">{fullName}</span>
            <span className="text-xs text-muted-foreground">{user.email}</span>
            <span className="mt-1 inline-flex w-fit items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-wider text-primary">
              {roleLabel}
            </span>
          </div>
          <div className="py-1">
            {isAdmin ? (
              <MenuItem
                icon={<ShieldCheck className="h-4 w-4 text-primary" />}
                onSelect={() => navigate("/admin/users")}
              >
                Admin panel
              </MenuItem>
            ) : null}
            {isRoyalTeamMember ? (
              // Royal-internal tool — kept out of the shared admin nav so
              // Mount Sinai users never see it.
              <MenuItem
                icon={<RefreshCcw className="h-4 w-4 text-primary" />}
                onSelect={() => navigate("/admin/tms-cleanup")}
              >
                TMS Cleanup
              </MenuItem>
            ) : null}
            <MenuItem
              icon={<User className="h-4 w-4 text-muted-foreground" />}
              onSelect={() => navigate("/account")}
            >
              Account settings
            </MenuItem>
            {/* Theme submenu — opens a side panel with the three
                modes. Live: clicking writes through to localStorage
                + the server immediately, so the menu stays open and
                shows the new check next to whatever was just
                picked. */}
            <Dropdown.Sub>
              <Dropdown.SubTrigger
                className={cn(
                  "flex cursor-default select-none items-center justify-between gap-2.5 rounded-md px-3 py-2 text-sm outline-none transition",
                  "text-foreground data-[highlighted]:bg-foreground/[0.07] data-[state=open]:bg-foreground/[0.07]",
                )}
              >
                <span className="inline-flex items-center gap-2.5">
                  <Palette className="h-4 w-4 text-muted-foreground" />
                  Theme
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                  {themeLabel(preference)}
                  <ChevronRight className="h-3.5 w-3.5" />
                </span>
              </Dropdown.SubTrigger>
              <Dropdown.Portal>
                <Dropdown.SubContent
                  sideOffset={6}
                  alignOffset={-4}
                  className="z-50 min-w-[14rem] overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-2xl"
                >
                  <ThemeMenuItem
                    value="light"
                    active={preference === "light"}
                    onPick={setPreference}
                  />
                  <ThemeMenuItem
                    value="dark"
                    active={preference === "dark"}
                    onPick={setPreference}
                  />
                  <ThemeMenuItem
                    value="system"
                    active={preference === "system"}
                    onPick={setPreference}
                  />
                </Dropdown.SubContent>
              </Dropdown.Portal>
            </Dropdown.Sub>
            {/* Royal-internal TripLink/TMS sync actions — same audience
                as TMS Cleanup, kept out of the Bookings toolbar so they
                don't confuse Mount Sinai staff. The section renders its
                own leading separator + "internal" header. */}
            {isRoyalTeamMember ? (
              <SyncTriplinkMenuSection sync={sync} />
            ) : null}
            {/* Royal-internal Billing review — same audience as the
                TripLink sync section + TMS Cleanup (isRoyalTeamMember:
                admin/super_admin AND Royal department). Lets the team
                add the standby-vehicles charge so the portal's grand
                total matches the TMS invoice. Its own separator +
                "internal" header mirror the TripLink section above. */}
            {isRoyalTeamMember ? (
              <>
                <Dropdown.Separator className="my-1 h-px bg-border" />
                <Dropdown.Label className="flex flex-col gap-0.5 px-3 pb-1 pt-1.5">
                  <span className="inline-flex items-center gap-1.5 text-[0.7rem] font-semibold uppercase tracking-wider text-muted-foreground">
                    <Receipt className="h-3.5 w-3.5" aria-hidden />
                    Billing · internal
                  </span>
                </Dropdown.Label>
                <MenuItem
                  icon={<Receipt className="h-4 w-4 text-primary" />}
                  onSelect={() => navigate("/admin/billing")}
                >
                  Billing
                </MenuItem>
              </>
            ) : null}
            <Dropdown.Separator className="my-1 h-px bg-border" />
            <MenuItem
              icon={<MessageSquare className="h-4 w-4 text-primary" />}
              onSelect={() => setFeedbackOpen(true)}
            >
              Send feedback
            </MenuItem>
            <Dropdown.Separator className="my-1 h-px bg-border" />
            <MenuItem
              icon={<LogOut className="h-4 w-4 text-destructive" />}
              onSelect={handleSignOut}
            >
              Sign out
            </MenuItem>
          </div>
        </Dropdown.Content>
      </Dropdown.Portal>
      {/* Feedback dialog is mounted at the AccountMenu level so it
          isn't tied to the Radix dropdown's open state (which closes
          as soon as you click "Send feedback"). It's a sibling of the
          dropdown's `<Dropdown.Root>`, so its portal lives at the top
          of the DOM regardless of dropdown lifecycle. */}
      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </Dropdown.Root>
  );
}

interface MenuItemProps {
  children: React.ReactNode;
  icon: React.ReactNode;
  onSelect?: () => void;
  disabled?: boolean;
}

function MenuItem({ children, icon, onSelect, disabled }: MenuItemProps) {
  return (
    <Dropdown.Item
      disabled={disabled}
      onSelect={(e) => {
        if (disabled) return;
        e.preventDefault();
        onSelect?.();
      }}
      className={cn(
        "flex cursor-default select-none items-center gap-2.5 rounded-md px-3 py-2 text-sm outline-none transition",
        disabled
          ? "cursor-not-allowed text-muted-foreground/60"
          : "text-foreground data-[highlighted]:bg-foreground/[0.07]",
      )}
    >
      {icon}
      <span>{children}</span>
    </Dropdown.Item>
  );
}

function friendlyRole(role: string): string {
  switch (role) {
    case "super_admin":
      return "Super Admin";
    case "admin":
      return "Admin";
    case "user":
    default:
      return "User";
  }
}

// =============================================================================
// Theme submenu helpers
// =============================================================================

function themeLabel(pref: ResolvedThemePreference): string {
  if (pref === "light") return "Light";
  if (pref === "dark") return "Dark";
  return "System";
}

function themeIcon(pref: ResolvedThemePreference) {
  if (pref === "light") return <Sun className="h-4 w-4 text-amber-700 dark:text-amber-300" />;
  if (pref === "dark") return <Moon className="h-4 w-4 text-foreground/85" />;
  return <Monitor className="h-4 w-4 text-muted-foreground" />;
}

function themeDescription(pref: ResolvedThemePreference): string {
  if (pref === "light") return "Bright UI, easier on bright rooms.";
  if (pref === "dark") return "Original navy look.";
  return "Follow your OS — updates live when it flips.";
}

function ThemeMenuItem({
  value,
  active,
  onPick,
}: {
  value: ResolvedThemePreference;
  active: boolean;
  onPick: (next: ResolvedThemePreference) => void;
}) {
  return (
    <Dropdown.Item
      onSelect={(e) => {
        // Stop Radix from auto-closing the parent menu — the user
        // might want to A/B between Light and System without having
        // to re-open the avatar dropdown each time.
        e.preventDefault();
        onPick(value);
      }}
      className="flex cursor-default select-none items-start gap-2.5 px-3 py-2.5 text-sm outline-none transition data-[highlighted]:bg-foreground/[0.07]"
    >
      <span className="mt-0.5">{themeIcon(value)}</span>
      <div className="flex flex-1 flex-col">
        <span className="font-semibold text-foreground">{themeLabel(value)}</span>
        <span className="text-xs text-muted-foreground">
          {themeDescription(value)}
        </span>
      </div>
      {active ? (
        <Check className="mt-1 h-4 w-4 flex-shrink-0 text-primary" />
      ) : (
        <span className="mt-1 h-4 w-4 flex-shrink-0" aria-hidden="true" />
      )}
    </Dropdown.Item>
  );
}
